import { useState } from 'react';
import { housesApi, type AllocationPlan } from '../lib/leadershipApi';

/**
 * Putting a whole school into houses.
 *
 * ⚠ PLAN, THEN APPLY — never one button. Nobody should press something once and have six hundred
 * children silently reorganised, and a head who cannot see the plan first will not press it at all.
 * The preview is not a nicety; it is what makes the feature usable by somebody accountable for the
 * outcome.
 *
 * ⚠ AND THE DANGEROUS OPTION IS OFF, LABELLED, AND CONFIRMED SEPARATELY. "Include children who
 * already have a house" is the difference between placing the unplaced and reshuffling everybody. A
 * child who has been in Sahyadri for three years has a house the way they have a surname — friends,
 * a badge on their bag, two Sports Days. Moving them so the arithmetic comes out neater takes
 * something from a ten-year-old to make a number tidier.
 */
export function HouseAllocationPanel({ institutionId, onDone }: { institutionId: string | null; onDone: () => void }) {
  const [plan, setPlan] = useState<AllocationPlan | null>(null);
  const [includeAssigned, setIncludeAssigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const preview = async (withAssigned: boolean) => {
    setBusy(true); setErr(null);
    if (!institutionId) { setErr('Choose a campus first.'); setBusy(false); return; }
    try { setPlan(await housesApi.allocationPlan(institutionId, withAssigned)); }
    catch { setErr('Could not work out an allocation.'); setPlan(null); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!plan) return;
    const moving = plan.moves.length;
    // ⚠ The confirm names the number and the irreversibility. "Are you sure?" tells nobody anything.
    const warn = includeAssigned
      ? `\n\nThis includes children who ALREADY have a house. They will be moved.`
      : '';
    if (!window.confirm(`Place ${moving} student${moving === 1 ? '' : 's'} into houses?${warn}\n\nThere is no undo — a wrong allocation is corrected by reassigning children individually.`)) return;
    setBusy(true); setErr(null);
    if (!institutionId) return;
    try { await housesApi.allocationApply(institutionId, includeAssigned); setPlan(null); onDone(); }
    catch { setErr('Could not apply the allocation.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong>Put students into houses</strong>
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
          Balanced across houses and across each class, with siblings kept together.
        </span>
        <button className="btn" style={{ marginLeft: 'auto' }} disabled={busy}
          onClick={() => preview(includeAssigned)}>
          {busy ? 'Working…' : 'Preview'}
        </button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
        <input type="checkbox" checked={includeAssigned}
          onChange={(e) => { setIncludeAssigned(e.target.checked); setPlan(null); }} />
        <span>
          Also move children who already have a house
          <span style={{ color: 'var(--ink-3)' }}> — only for a first setup; it breaks existing house identity</span>
        </span>
      </label>

      {err && <p style={{ color: 'var(--bad, #b3261e)', fontSize: 13 }}>{err}</p>}

      {/* ⚠ Array.isArray, NOT truthiness. An endpoint that answers with the WRONG SHAPE — a stub, a
          proxy, a broad route matching a narrow one — hands back a truthy object with no `moves` on
          it, and `plan.moves.length` crashes the screen. That is not hypothetical: it happened here
          while this was being built, because a fixture key matched more URLs than it meant to. */}
      {plan && Array.isArray(plan.moves) && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line, #e6e8ec)', paddingTop: 12 }}>
          {plan.moves.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>
              Nothing to do — every student already has a house.
            </p>
          ) : (
            <>
              <p style={{ margin: '0 0 10px', fontSize: 13 }}>
                <strong>{plan.moves.length}</strong> student{plan.moves.length === 1 ? '' : 's'} would be placed.
                {plan.alreadyAssigned > 0 && !includeAssigned &&
                  <span style={{ color: 'var(--ink-3)' }}> {plan.alreadyAssigned} already have a house and are left alone.</span>}
              </p>

              {/* ⚠ BEFORE AND AFTER TOGETHER. A single "after" column looks balanced whatever it
                  says; the pair is what lets somebody see the change is what they expected. */}
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                {plan.after.map((a) => {
                  const b = plan.before.find((x) => x.houseId === a.houseId);
                  const delta = a.members - (b?.members ?? 0);
                  return (
                    <div key={a.houseId} style={{ fontSize: 13, paddingLeft: 8, borderLeft: `4px solid ${a.colorHex}` }}>
                      <div style={{ fontWeight: 600 }}>{a.houseName}</div>
                      <div style={{ color: 'var(--ink-3)' }}>
                        {b?.members ?? 0} → <strong style={{ color: 'var(--ink-1)' }}>{a.members}</strong>
                        {delta > 0 && <span> (+{delta})</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {plan.notes.map((n, i) => (
                <p key={i} style={{ margin: '2px 0', fontSize: 12.5, color: 'var(--ink-3)' }}>{n}</p>
              ))}

              {/* A sample, not all six hundred — enough to see the shape and the sibling rule working. */}
              <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 10 }}>
                {plan.moves.slice(0, 40).map((m) => (
                  <div key={m.studentId} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
                    <span style={{ flex: 1 }}>{m.fullName}</span>
                    <span style={{ color: 'var(--ink-3)' }}>{m.classLabel ?? 'no class'}</span>
                    <span style={{ fontWeight: 600, minWidth: 90 }}>{m.houseName}</span>
                    <span style={{ color: 'var(--ink-3)', minWidth: 110 }}>{m.because ?? ''}</span>
                  </div>
                ))}
                {plan.moves.length > 40 && (
                  <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    …and {plan.moves.length - 40} more.
                  </p>
                )}
              </div>

              <button className="btn btn-accent" style={{ marginTop: 12 }} disabled={busy} onClick={apply}>
                Apply this allocation
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
